import { useMemo } from 'react';
import { resolveKinds } from '@domain/entities/AreaCatalog';
import type { IncapacityKind } from '@domain/entities/Incapacity';
import type { PermissionKind } from '@domain/entities/PermissionRequest';
import { useRuntimeConfig } from '../providers/RuntimeConfigProvider';

/**
 * Los tipos de incapacidad y de permiso que aplican en un área.
 *
 * Un solo sitio donde se junta el catálogo de la empresa con lo que el área
 * cambió, porque hasta tres pantallas preguntan lo mismo —los dos asistentes y
 * la de administrar el área— y tres formas de resolverlo son tres formas de que
 * una discrepe. El servidor resuelve lo mismo por su cuenta en
 * `requireCatalogEntry`, que es lo que de verdad decide: esto sólo gobierna lo
 * que se ofrece.
 *
 * Devuelve **todo**, encendido y apagado. Quien pinta un asistente filtra por
 * `enabled`; quien administra el área necesita ver lo apagado para poder
 * encenderlo.
 */
export const useAreaKinds = (
  areaId: string,
): {
  readonly incapacityKinds: readonly IncapacityKind[];
  readonly permissionKinds: readonly PermissionKind[];
} => {
  const config = useRuntimeConfig();

  return useMemo(() => {
    const propio = config.areaCatalogs[areaId];
    return {
      incapacityKinds: resolveKinds(
        config.catalogs.incapacityKinds,
        propio?.incapacityKinds ?? [],
      ),
      permissionKinds: resolveKinds(
        config.catalogs.permissionKinds,
        propio?.permissionKinds ?? [],
      ),
    };
  }, [config.catalogs.incapacityKinds, config.catalogs.permissionKinds, config.areaCatalogs, areaId]);
};
